'use client';
// src/components/productPage/singleProductPageComponents/ysim/useYsimData.ts
import { useEffect, useState } from 'react';
import axios from 'axios';
import type { ApiResponse, ChartDatasetWithAxis, KsimState } from './types';
import { DEFAULT_SELECTED, LABELS } from './types';

const COLORS = [
    { borderColor: '#4A90E2', backgroundColor: 'rgba(74, 144, 226, 0.4)' },
    { borderColor: '#D64545', backgroundColor: 'rgba(214, 69, 69, 0.4)' },
    { borderColor: '#50B83C', backgroundColor: 'rgba(80, 184, 60, 0.4)' },
    { borderColor: '#F7B731', backgroundColor: 'rgba(247, 183, 49, 0.4)' },
    { borderColor: '#8E44AD', backgroundColor: 'rgba(142, 68, 173, 0.4)' },
    { borderColor: '#E67E22', backgroundColor: 'rgba(230, 126, 34, 0.4)' },
    { borderColor: '#34495E', backgroundColor: 'rgba(52, 73, 94, 0.4)' },
    { borderColor: '#16A085', backgroundColor: 'rgba(22, 160, 133, 0.4)' },
    { borderColor: '#FF6B6B', backgroundColor: 'rgba(255, 107, 107, 0.4)' },
    { borderColor: '#9B59B6', backgroundColor: 'rgba(155, 89, 182, 0.4)' },
    { borderColor: '#2C3E50', backgroundColor: 'rgba(44, 62, 80, 0.4)' },
    { borderColor: '#1ABC9C', backgroundColor: 'rgba(26, 188, 156, 0.4)' },
    { borderColor: '#5D4037', backgroundColor: 'rgba(93, 64, 55, 0.4)' },
    { borderColor: '#2E7D32', backgroundColor: 'rgba(46, 125, 50, 0.4)' },
    { borderColor: '#1976D2', backgroundColor: 'rgba(25, 118, 210, 0.4)' },
    { borderColor: '#00BCD4', backgroundColor: 'rgba(0, 188, 212, 0.4)' },
];

export function useKsimData(partNumber: string) {
    const [state, setState] = useState<KsimState>({
        allDatasets: [],
        plotData: { datasets: [] },
        selectedDatasets: DEFAULT_SELECTED,
        loading: true,
        error: null,
    });

    // 切換勾選
    const toggleDataset = (datasetLabel: string) => {
        setState((prev) => {
            const sd = prev.selectedDatasets;

            // 單選族群：TCC / VCAC / VBIAS / RIPPLE
            if (
                datasetLabel === LABELS.TCC ||
                datasetLabel === LABELS.VCAC ||
                datasetLabel === LABELS.VBIAS ||
                datasetLabel === LABELS.RIPPLE
            ) {
                if (!sd.includes(datasetLabel)) {
                    return {
                        ...prev,
                        selectedDatasets: [datasetLabel],
                    };
                }
            } else {
                // 切換到一般曲線時，若原本是單選族群，清掉它們
                if (
                    sd.includes(LABELS.TCC) ||
                    sd.includes(LABELS.VCAC) ||
                    sd.includes(LABELS.VBIAS) ||
                    sd.includes(LABELS.RIPPLE)
                ) {
                    return { ...prev, selectedDatasets: [datasetLabel] };
                }
            }

            // 一般 toggle
            const next = sd.includes(datasetLabel)
                ? sd.filter((l) => l !== datasetLabel)
                : [...sd, datasetLabel];

            return { ...prev, selectedDatasets: next };
        });
    };

    // 反映勾選到 plotData
    useEffect(() => {
        if (state.allDatasets.length === 0) return;
        const selected: ChartDatasetWithAxis[] = state.allDatasets
            .filter((d) => state.selectedDatasets.includes(d.label))
            .map((d) => ({ ...d }));

        // Ripple alt label
        if (state.selectedDatasets.includes(LABELS.RIPPLE)) {
            selected.forEach((s) => {
                if (s.altLabel) s.label = s.altLabel;
            });
        }

        setState((prev) => ({ ...prev, plotData: { datasets: selected as any } }));
    }, [state.selectedDatasets, state.allDatasets]);

    // 抓資料
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const url = `https://search.kemet.com/sim/plot/${partNumber}`;
                //for local testing of ksim stuff
                // const url = `http://localhost/sim/plot/${partNumber}`;
                const { data } = await axios.get<ApiResponse>(url);

                const fv = data.frequency_values;
                if (!fv || !Array.isArray(fv) || fv.length === 0) {
                    throw new Error('Invalid or empty data received from API.');
                }
                const freqs = fv.map((x) => x.frequency);

                const ds: ChartDatasetWithAxis[] = [
                    {
                        label: 'Impedance (Ohms)',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.impedance } : null))
                            .filter(Boolean),
                        ...COLORS[0],
                        fill: false,
                        tension: 0.1,
                    },
                    {
                        label: 'ESR (Ohms)',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.esr } : null))
                            .filter(Boolean),
                        ...COLORS[1],
                        fill: false,
                        tension: 0.1,
                    },
                    {
                        label: 'Capacitance (F)',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.capacitance } : null))
                            .filter(Boolean),
                        ...COLORS[2],
                        fill: false,
                        tension: 0.1,
                    },
                    {
                        label: 'Inductance (H)',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.inductance } : null))
                            .filter(Boolean),
                        ...COLORS[9],
                        fill: false,
                        tension: 0.1,
                    },
                ];

                if (fv[0]?.current != null) {
                    ds.push({
                        label: 'Current (A)',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.current } : null))
                            .filter(Boolean),
                        ...COLORS[3],
                        fill: false,
                        tension: 0.1,
                    });
                }
                if (fv[0]?.voltage != null) {
                    ds.push({
                        label: 'Voltage (V)',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.voltage } : null))
                            .filter(Boolean),
                        ...COLORS[4],
                        fill: false,
                        tension: 0.1,
                        yAxisID: 'y',
                    });
                }
                if (fv[0]?.s11_magnitude !== undefined) {
                    ds.push({
                        label: 'S11 Magnitude',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.s11_magnitude } : null))
                            .filter(Boolean),
                        ...COLORS[5],
                        fill: false,
                        tension: 0.1,
                        yAxisID: 'y',
                    });
                }
                if (fv[0]?.s11_phase !== undefined) {
                    ds.push({
                        label: 'S11 Phase (Degrees)',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.s11_phase } : null))
                            .filter(Boolean),
                        ...COLORS[6],
                        fill: false,
                        tension: 0.1,
                        yAxisID: 'y',
                    });
                }
                if (fv[0]?.s21_magnitude !== undefined) {
                    ds.push({
                        label: 'S21 Magnitude',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.s21_magnitude } : null))
                            .filter(Boolean),
                        ...COLORS[7],
                        fill: false,
                        tension: 0.1,
                        yAxisID: 'y',
                    });
                }
                if (fv[0]?.s21_phase !== undefined) {
                    ds.push({
                        label: 'S21 Phase (Degrees)',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.s21_phase } : null))
                            .filter(Boolean),
                        ...COLORS[8],
                        fill: false,
                        tension: 0.1,
                        yAxisID: 'y',
                    });
                }
                if (fv[0]?.q !== undefined) {
                    ds.push({
                        label: 'Q Value',
                        data: freqs
                            .map((f, i) => (fv[i] ? { x: f, y: fv[i]?.q } : null))
                            .filter(Boolean),
                        ...COLORS[15],
                        fill: false,
                        tension: 0.1,
                        yAxisID: 'y',
                    });
                }

                if (data.tcc_values) {
                    ds.push({
                        label: LABELS.TCC,
                        data: data.tcc_values
                            .map((x) => ({ x: x.temperature, y: x.capacitance_change_percent }))
                            .filter(Boolean),
                        ...COLORS[8],
                        fill: false,
                        tension: 0.4,
                        yAxisID: 'y',
                    });
                }
                if (data.vcac_values) {
                    ds.push({
                        label: LABELS.VCAC,
                        data: data.vcac_values
                            .map((x) => ({ x: x.vcac, y: x.capacitance_change_percent }))
                            .filter(Boolean),
                        ...COLORS[10],
                        fill: false,
                        tension: 0.4,
                        yAxisID: 'y',
                    });
                }
                if (data.vbias_values) {
                    ds.push({
                        label: LABELS.VBIAS,
                        data: data.vbias_values
                            .map((x) => ({ x: x.vbias, y: x.capacitance_change_percent }))
                            .filter(Boolean),
                        ...COLORS[11],
                        fill: false,
                        tension: 0.4,
                        yAxisID: 'y',
                    });
                }

                const ripple = data.ripple_current ?? {};
                let colorIndex = 12;
                Object.entries(ripple)
                    .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                    .forEach(([key, arr]) => {
                        ds.push({
                            label: LABELS.RIPPLE,
                            altLabel: key,
                            data: arr
                                .map((x) => ({ x: x.current, y: x.temperature }))
                                .filter(Boolean),
                            ...COLORS[colorIndex],
                            fill: false,
                            tension: 0.4,
                            yAxisID: 'y',
                        });
                        colorIndex++;
                    });

                if (!cancelled) {
                    setState((prev) => ({
                        ...prev,
                        allDatasets: ds,
                        plotData: {
                            datasets: ds.filter((d) => DEFAULT_SELECTED.includes(d.label)) as any,
                        },
                        loading: false,
                        error: null,
                    }));
                }
            } catch (e: any) {
                if (!cancelled) {
                    setState((prev) => ({
                        ...prev,
                        loading: false,
                        error: e?.message ?? 'Fetch error',
                    }));
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [partNumber]);

    return {
        state,
        setSelectedDatasets: (next: string[]) =>
            setState((prev) => ({ ...prev, selectedDatasets: next })),
        toggleDataset,
    };
}
